import { useClavesSatConsultadas } from "@/features/catalogos/hooks/use-claves-sat";
import { IconAlertTriangle } from "@tabler/icons-react";
import type { Prorrateo } from "@/lib/types";
import { Alert, Card, Divider, Group, Stack } from "@/components/ui";
import { calcularDiferencia } from "../utils/suma";
import { evaluarConfirmable } from "../utils/conceptos";
import { ConceptosTable } from "./ConceptosTable";
import { CfdiSeccionHeader } from "./cfdi-section/CfdiSeccionHeader";
import { ConfirmarCfdiBoton } from "./cfdi-section/ConfirmarCfdiBoton";
import { SumaResumenBanner } from "./SumaResumenBanner";
import { AvisoProrrateoAutomatico } from "./AvisoProrrateoAutomatico";

/**
 * Sección del prorrateo de UN slot CFDI (subtipo Múltiples CFDI) · la página
 * `/prorrateo-multiple` renderiza N de éstas. Rediseño conceptos→sub-líneas:
 * cada CFDI de CA tiene un solo concepto; FB60 puede tener varios · cada concepto
 * se divide en sub-líneas que deben sumar su importe.
 */
export function ProrrateoSeccion({
  solicitudId,
  numeroCfdi,
  numeroSolicitud,
  uuid,
  rfcEmisor,
  subtotalCfdi,
  prorrateo,
  onCambio,
  soloLectura = false,
}: {
  solicitudId: string;
  numeroCfdi: number;
  numeroSolicitud: number;
  uuid: string;
  rfcEmisor: string;
  subtotalCfdi: number;
  prorrateo: Prorrateo | null;
  onCambio: () => void;
  soloLectura?: boolean;
}) {
  const isConfirmado = prorrateo?.estado === "Confirmado";
  const lineasBloqueadas = isConfirmado || soloLectura;
  const diferencia = calcularDiferencia(prorrateo?.sumaTotal ?? 0, subtotalCfdi);
  const { catalogadas } = useClavesSatConsultadas((prorrateo?.lineas ?? []).map((l) => l.claveSat));
  const confirmable = evaluarConfirmable(prorrateo?.lineas ?? [], catalogadas);

  return (
    <Card
      padding="lg"
      style={{
        borderColor: isConfirmado ? "var(--color-exito-400)" : undefined,
        background: isConfirmado ? "color-mix(in srgb, var(--color-exito-600) 6%, transparent)" : undefined,
      }}
    >
      <Stack gap="md">
        <CfdiSeccionHeader
          solicitudId={solicitudId}
          numeroCfdi={numeroCfdi}
          uuid={uuid}
          rfcEmisor={rfcEmisor}
          subtotalCfdi={subtotalCfdi}
          estadoProrrateo={prorrateo?.estado ?? "SinConstruir"}
          isConfirmado={isConfirmado}
          // Un prorrateo automático no se reabre (el backend lo rechaza) · sin botón «Reabrir».
          soloLectura={soloLectura || !!prorrateo?.confirmadoAutomaticamente}
          onCambio={onCambio}
        />
        <AvisoProrrateoAutomatico prorrateo={prorrateo} />

        {prorrateo === null ? (
          <Alert variant="warning" icon={<IconAlertTriangle size={16} stroke={1.7} />}>
            Aún no se construyó el prorrateo · asigna el proveedor de este RFC primero.
          </Alert>
        ) : (
          <>
            <Divider />
            <SumaResumenBanner diferencia={diferencia} />
            <ConceptosTable
              lineas={prorrateo.lineas}
              disabled={lineasBloqueadas}
              solicitudId={solicitudId}
              numeroSolicitud={numeroSolicitud}
              numeroCfdi={numeroCfdi}
              compacto
              validarClavesSat
            />
            {!lineasBloqueadas ? (
              <Group justify="flex-end">
                <ConfirmarCfdiBoton
                  solicitudId={solicitudId}
                  numeroCfdi={numeroCfdi}
                  habilitado={confirmable.ok}
                  motivo={confirmable.motivo}
                  onConfirmado={onCambio}
                />
              </Group>
            ) : null}
          </>
        )}
      </Stack>
    </Card>
  );
}
